import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_URL,
  FEATURE_ANNOUNCEMENTS_LIST,
  ADD_FEATURE_ANNOUNCEMENTS,
  DELETE_FEATURE_ANNOUNCEMENTS,
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  featureAnnouncementsList: [],
  submitLoading: false,
  error: "",
};

export function normalizeListPayload(payload) {
  if (!payload) return [];
  const list =
    payload?.data ??
    payload?.feature_announcements ??
    payload?.feature_announcement_data ??
    payload?.result ??
    payload?.announcements ??
    (Array.isArray(payload) ? payload : []);
  return Array.isArray(list) ? list : [];
}

function isListAllPayloadId(id) {
  return (
    id === undefined ||
    id === null ||
    String(id).trim() === ""
  );
}

/**
 * Same API for list and single row: `Feature_announcements_api/get_all_feature_announcements`.
 * Pass `id: ""` (or omit) for all rows; pass `id` for one record.
 * Token is stripped before POST body.
 */
export const fetchFeatureAnnouncementsList = createAsyncThunk(
  "featureAnnouncement/fetchFeatureAnnouncementsList",
  async (data, { rejectWithValue }) => {
    const { token, id, ...rest } = data;
    const body = {
      ...rest,
      id: isListAllPayloadId(id) ? "" : String(id),
    };
    try {
      const response = await axios.post(
        BASE_URL + FEATURE_ANNOUNCEMENTS_LIST,
        body,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

/**
 * Add or update — same endpoint. Pass a fully built FormData from the form.
 * Include `Authorization` via token in thunk args: `{ token, formData }`.
 */
export const addOrUpdateFeatureAnnouncement = createAsyncThunk(
  "featureAnnouncement/addOrUpdateFeatureAnnouncement",
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + ADD_FEATURE_ANNOUNCEMENTS,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const body = response.data;
      const st = body?.status;
      const ok =
        st === true ||
        st === "true" ||
        st === "Success" ||
        st === 1 ||
        String(st).toLowerCase() === "success";
      if (ok) {
        return body;
      }
      return rejectWithValue(body || { message: "Request failed" });
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deleteFeatureAnnouncement = createAsyncThunk(
  "featureAnnouncement/deleteFeatureAnnouncement",
  async (data, { rejectWithValue }) => {
    const { token, id } = data;
    const formData = new FormData();
    if (id != null) formData.append("id", String(id));
    try {
      const response = await axios.post(
        BASE_URL + DELETE_FEATURE_ANNOUNCEMENTS,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const body = response.data;
      const st = body?.status;
      const ok =
        st === true ||
        st === "true" ||
        st === "Success" ||
        st === 1 ||
        String(st).toLowerCase() === "success";
      if (ok) {
        return { id };
      }
      return rejectWithValue(body || { message: "Delete failed" });
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const featureAnnouncementSlice = createSlice({
  name: "featureAnnouncement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchFeatureAnnouncementsList.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(fetchFeatureAnnouncementsList.fulfilled, (state, action) => {
      state.loading = false;
      const requestedId = action.meta.arg?.id;
      if (isListAllPayloadId(requestedId)) {
        state.featureAnnouncementsList = normalizeListPayload(action.payload);
      }
      state.error = "";
    });
    builder.addCase(fetchFeatureAnnouncementsList.rejected, (state, action) => {
      state.loading = false;
      const requestedId = action.meta.arg?.id;
      if (isListAllPayloadId(requestedId)) {
        state.featureAnnouncementsList = [];
      }
      state.error =
        action.payload?.message || action.error?.message || "Unable to fetch list";
    });

    builder.addCase(addOrUpdateFeatureAnnouncement.pending, (state) => {
      state.submitLoading = true;
      state.error = "";
    });
    builder.addCase(addOrUpdateFeatureAnnouncement.fulfilled, (state) => {
      state.submitLoading = false;
      state.error = "";
    });
    builder.addCase(addOrUpdateFeatureAnnouncement.rejected, (state, action) => {
      state.submitLoading = false;
      state.error =
        action.payload?.message || action.error?.message || "Save failed";
    });

    builder.addCase(deleteFeatureAnnouncement.fulfilled, (state, action) => {
      const removedId = action.payload?.id;
      if (removedId != null) {
        state.featureAnnouncementsList = state.featureAnnouncementsList.filter(
          (row) => {
            const rid =
              row?.id ?? row?.feature_announcement_id ?? row?.announcement_id;
            return String(rid) !== String(removedId);
          }
        );
      }
    });
  },
});

export default featureAnnouncementSlice.reducer;
